import type { DocumentStatus, DocumentType } from "shared"

const READ_ONLY_MESSAGE = "Суб'єкт виключений з реєстру — доступний лише перегляд."
const ANNUL_WINDOW_MS = 24 * 60 * 60 * 1000

export type AnnulActionAvailability =
  | { kind: "hidden" }
  | { kind: "blocked"; message: string }
  | { kind: "allowed" }

/**
 * warehouse.md's centralized `annulActionAvailability` — the same three-state
 * gate (hidden/blocked/allowed) `documents.md`'s row action and document page
 * defer to, ported here rather than left as a "no backend data" gap: the
 * linked document's real status/type/signedAt come from a real endpoint
 * (`getOtkDocumentById`, see `store/warehouse.ts`'s `fetchDocumentSummary`),
 * just not embedded in the blanks list response itself.
 *
 * `document` is `undefined` while its summary hasn't loaded yet (or failed)
 * — rendered as `hidden` rather than a flash of a button that immediately
 * disables, since there's nothing to show a reason for yet.
 *
 * Deviates from the page spec in one respect, stated rather than hidden:
 * there's no "недійсність реєстру" (registry-invalidity) signal anywhere in
 * this app yet, so that check is simply absent — every other real gate
 * (status, type, permission, read-only, the 24-hour KEP window) is applied.
 */
export function annulActionAvailability({
  document,
  hasPermission,
  readOnly,
}: {
  document:
    | { status: DocumentStatus; type: DocumentType; signedAt?: string | null }
    | undefined
  hasPermission: boolean
  readOnly: boolean
}): AnnulActionAvailability {
  if (!document) return { kind: "hidden" }
  if (document.type !== "PROTOCOL") return { kind: "hidden" }
  if (readOnly) return { kind: "blocked", message: READ_ONLY_MESSAGE }
  if (!hasPermission) {
    return { kind: "blocked", message: "Немає права підпису та надсилання до ЄДРТЗ." }
  }
  if (document.status !== "SENT" && document.status !== "DELIVERED") {
    return {
      kind: "blocked",
      message: "Документ у статусі, який не підлягає ануляції.",
    }
  }
  if (!document.signedAt) {
    return {
      kind: "blocked",
      message: "Немає підтвердженого моменту підпису КЕП.",
    }
  }
  const deadline = new Date(document.signedAt).getTime() + ANNUL_WINDOW_MS
  if (Number.isNaN(deadline) || Date.now() >= deadline) {
    return {
      kind: "blocked",
      message: "Строк ануляції (24 години від моменту підпису КЕП) минув.",
    }
  }
  return { kind: "allowed" }
}
